import { useState, useEffect } from 'react';
import { format, parseISO, isPast, isBefore, addDays } from 'date-fns';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Clock, Syringe, Scissors, Stethoscope, Activity, AlertCircle, Plus, Bell, Calendar, MoreHorizontal, CheckCircle2, Trash2 } from 'lucide-react';
import { useAuth, getActiveAccountHeader } from '../App';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Helper to format record type for display
const formatRecordType = (recordType) => {
  const typeMap = {
    'appointment': 'Appointment',
    'vaccination': 'Vaccination',
    'worming': 'Worming',
    'farrier': 'Farrier',
    'dentist': 'Dentist',
    'veterinary': 'Veterinary',
    'bodywork': 'Body Work',
    'medication': 'Medication',
    'insemination': 'Insemination',
    'pregnancy_scan': 'Pregnancy Scan',
    'foaling': 'Foaling',
    'other': 'Other'
  };
  return typeMap[recordType] || recordType?.replace(/_/g, ' ') || recordType;
};

export const Reminders = () => {
  const { token } = useAuth();
  const [reminders, setReminders] = useState([]);
  const [horses, setHorses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [newReminder, setNewReminder] = useState({
    horse_id: '',
    record_type: 'appointment',
    appointment_type: '',
    appointment_type_other: '',
    provider: '',
    date: '',
    next_due_date: '',
    appointment_time: '',
    notes: ''
  });

  // Get headers dynamically to ensure fresh activeAccountId after account switch
  const getHeaders = () => {
    const activeAccountId = localStorage.getItem('activeAccountId') || sessionStorage.getItem('activeAccountId');
    const h = { 'Content-Type': 'application/json' };
    if (activeAccountId) h['X-Active-Account'] = activeAccountId;
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    const headers = getHeaders();
    try {
      const [remindersRes, horsesRes] = await Promise.all([
        fetch(`${API}/dashboard/reminders`, { headers }),
        fetch(`${API}/horses`, { headers })
      ]);
      
      if (remindersRes.ok) {
        const data = await remindersRes.json();
        // API returns {upcoming_health: [...]} so extract the array
        const remindersArray = data.upcoming_health || data || [];
        // Sort by closest due date first
        const sorted = Array.isArray(remindersArray) ? remindersArray.sort((a, b) => {
          if (!a.next_due_date) return 1;
          if (!b.next_due_date) return -1;
          return new Date(a.next_due_date) - new Date(b.next_due_date);
        }) : [];
        setReminders(sorted);
      }
      if (horsesRes.ok) {
        const data = await horsesRes.json();
        // Only show current horses (not archived/former)
        setHorses(data.filter(h => !h.is_deceased && h.status !== 'archived' && h.horse_tab !== 'former'));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddReminder = async (e) => {
    e.preventDefault();
    if (!newReminder.next_due_date) {
      toast.error('Due date is required for reminders');
      return;
    }
    if (newReminder.record_type === 'appointment' && !newReminder.appointment_type) {
      toast.error('Please select an appointment type');
      return;
    }

    try {
      // For appointments, include type and time in notes/provider
      let notes = newReminder.notes || '';
      let provider = newReminder.provider || '';
      
      if (newReminder.record_type === 'appointment') {
        // Store appointment type info
        const apptType = newReminder.appointment_type === 'other' 
          ? newReminder.appointment_type_other 
          : newReminder.appointment_type;
        
        // Prepend appointment type to provider field for display
        if (apptType) {
          provider = apptType.charAt(0).toUpperCase() + apptType.slice(1) + (provider ? ` - ${provider}` : '');
        }
        
        if (newReminder.appointment_time) {
          notes = `Time: ${newReminder.appointment_time}${notes ? '\n' + notes : ''}`;
        }
      }

      const payload = {
        ...newReminder,
        provider,
        notes,
        horse_id: newReminder.horse_id || null // null means general reminder
      };
      // Remove fields that shouldn't be sent to backend
      delete payload.appointment_time;
      delete payload.appointment_type;
      delete payload.appointment_type_other;

      const response = await fetch(`${API}/health-records`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Reminder added!');
        setDialogOpen(false);
        setNewReminder({
          horse_id: '',
          record_type: 'appointment',
          appointment_type: '',
          appointment_type_other: '',
          provider: '',
          date: '',
          next_due_date: '',
          appointment_time: '',
          notes: ''
        });
        fetchData();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add reminder');
      }
    } catch (error) {
      toast.error('Failed to add reminder');
    }
  };

  const handleEditReminder = async (e) => {
    e.preventDefault();
    if (!selectedReminder) return;
    try {
      const response = await fetch(`${API}/health-records/${selectedReminder.record_id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(selectedReminder)
      });
      if (response.ok) {
        toast.success('Reminder updated!');
        setEditDialogOpen(false);
        setSelectedReminder(null);
        fetchData();
      } else {
        toast.error('Failed to update reminder');
      }
    } catch (error) {
      toast.error('Failed to update reminder');
    }
  };

  const handleCompleteReminder = async (reminder, e) => {
    e.stopPropagation();
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await fetch(`${API}/health-records/${reminder.record_id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify({
          ...reminder,
          date: today,
          next_due_date: null
        })
      });
      if (response.ok) {
        toast.success(`${formatRecordType(reminder.record_type)} marked complete`);
        fetchData();
      } else {
        toast.error('Failed to complete reminder');
      }
    } catch (error) {
      toast.error('Failed to complete reminder');
    }
  };

  const handleDeleteReminder = async (recordId, e) => {
    e.stopPropagation();
    try {
      const response = await fetch(`${API}/health-records/${recordId}`, {
        method: 'DELETE',
        headers: getHeaders()
      });
      if (response.ok) {
        toast.success('Reminder deleted');
        fetchData();
      } else {
        toast.error('Failed to delete reminder');
      }
    } catch (error) {
      toast.error('Failed to delete reminder');
    }
  };

  const getRecordTypeIcon = (type) => {
    const icons = {
      appointment: <Calendar className="w-4 h-4" />,
      vaccination: <Syringe className="w-4 h-4" />,
      farrier: <Scissors className="w-4 h-4" />,
      dentist: <Stethoscope className="w-4 h-4" />,
      veterinary: <Stethoscope className="w-4 h-4" />,
      bodywork: <Activity className="w-4 h-4" />,
      worming: <Activity className="w-4 h-4" />,
      injury: <Activity className="w-4 h-4" />,
      medication: <Activity className="w-4 h-4" />,
      foaling: <Bell className="w-4 h-4" />,
      other: <MoreHorizontal className="w-4 h-4" />
    };
    return icons[type] || <Activity className="w-4 h-4" />;
  };

  const getRecordTypeClass = (type) => {
    const classes = {
      appointment: 'bg-indigo-100 text-indigo-700',
      vaccination: 'bg-emerald-100 text-emerald-700',
      farrier: 'bg-amber-100 text-amber-700',
      dentist: 'bg-sky-100 text-sky-700',
      veterinary: 'bg-teal-100 text-teal-700',
      bodywork: 'bg-pink-100 text-pink-700',
      worming: 'bg-violet-100 text-violet-700',
      injury: 'bg-rose-100 text-rose-700',
      medication: 'bg-blue-100 text-blue-700',
      foaling: 'bg-orange-100 text-orange-700',
      other: 'bg-gray-100 text-gray-700'
    };
    return classes[type] || 'bg-gray-100 text-gray-700';
  };

  const isUrgent = (dueDate) => {
    if (!dueDate) return false;
    const date = parseISO(dueDate);
    return isBefore(date, addDays(new Date(), 7));
  };

  const overdueReminders = reminders.filter(r => r.next_due_date && isPast(parseISO(r.next_due_date)));
  const upcomingReminders = reminders.filter(r => r.next_due_date && !isPast(parseISO(r.next_due_date)));
  const groupedReminders = reminders.reduce((acc, r) => {
    if (!acc[r.record_type]) acc[r.record_type] = [];
    acc[r.record_type].push(r);
    return acc;
  }, {});

  if (loading) {
    return <Layout><div className="flex items-center justify-center min-h-screen"><div className="animate-spin w-8 h-8 border-4 border-forest border-t-transparent rounded-full" /></div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-8 p-4 md:p-8 max-w-4xl mx-auto">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold font-serif text-foreground">Reminders</h1>
            <p className="text-muted-foreground mt-1">
              {reminders.length} upcoming care {reminders.length === 1 ? 'item' : 'items'}
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-forest hover:bg-forest-dark gap-2" data-testid="add-reminder-btn">
                <Plus className="w-4 h-4" /> Add Reminder
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-serif flex items-center gap-2">
                  <Bell className="w-5 h-5" /> Add New Reminder
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddReminder} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Assign to Horse (optional)</Label>
                  <Select value={newReminder.horse_id || "general"} onValueChange={(v) => setNewReminder({...newReminder, horse_id: v === "general" ? "" : v})}>
                    <SelectTrigger data-testid="reminder-horse-select">
                      <SelectValue placeholder="General (no horse)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General (no horse)</SelectItem>
                      {horses.map(h => (
                        <SelectItem key={h.horse_id} value={h.horse_id}>{h.stable_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Reminder Type *</Label>
                  <Select value={newReminder.record_type} onValueChange={(v) => setNewReminder({...newReminder, record_type: v, appointment_time: '', appointment_type: '', appointment_type_other: ''})}>
                    <SelectTrigger data-testid="reminder-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="appointment">Appointment</SelectItem>
                      <SelectItem value="vaccination">Vaccination</SelectItem>
                      <SelectItem value="farrier">Farrier</SelectItem>
                      <SelectItem value="dentist">Dentist</SelectItem>
                      <SelectItem value="bodywork">Body Work</SelectItem>
                      <SelectItem value="worming">Worming</SelectItem>
                      <SelectItem value="veterinary">Veterinary</SelectItem>
                      <SelectItem value="medication">Medication</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {newReminder.record_type === 'appointment' && (
                  <div className="space-y-2">
                    <Label>Appointment Type *</Label>
                    <Select value={newReminder.appointment_type} onValueChange={(v) => setNewReminder({...newReminder, appointment_type: v, appointment_type_other: v !== 'other' ? '' : newReminder.appointment_type_other})}>
                      <SelectTrigger data-testid="appointment-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="veterinary">Veterinary</SelectItem>
                        <SelectItem value="farrier">Farrier</SelectItem>
                        <SelectItem value="dentist">Dentist</SelectItem>
                        <SelectItem value="bodywork">Body Work</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                    {newReminder.appointment_type === 'other' && (
                      <Input 
                        value={newReminder.appointment_type_other} 
                        onChange={(e) => setNewReminder({...newReminder, appointment_type_other: e.target.value})}
                        placeholder="Specify appointment type"
                        className="mt-2"
                        data-testid="appointment-type-other-input"
                      />
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Provider / Vet Name</Label>
                  <Input 
                    value={newReminder.provider} 
                    onChange={(e) => setNewReminder({...newReminder, provider: e.target.value})}
                    placeholder="e.g., Dr. Smith"
                    data-testid="reminder-provider-input"
                  />
                </div>

                {newReminder.record_type === 'appointment' ? (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Date *</Label>
                      <Input 
                        type="date" 
                        value={newReminder.next_due_date} 
                        onChange={(e) => setNewReminder({...newReminder, next_due_date: e.target.value})}
                        required
                        data-testid="appointment-date-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Time</Label>
                      <Input 
                        type="time" 
                        value={newReminder.appointment_time || ''} 
                        onChange={(e) => setNewReminder({...newReminder, appointment_time: e.target.value})}
                        data-testid="appointment-time-input"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Last Done (optional)</Label>
                      <Input 
                        type="date" 
                        value={newReminder.date} 
                        onChange={(e) => setNewReminder({...newReminder, date: e.target.value})}
                        data-testid="reminder-date-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Next Due Date *</Label>
                      <Input 
                        type="date" 
                        value={newReminder.next_due_date} 
                        onChange={(e) => setNewReminder({...newReminder, next_due_date: e.target.value})}
                        required
                        data-testid="reminder-due-date-input"
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea 
                    value={newReminder.notes} 
                    onChange={(e) => setNewReminder({...newReminder, notes: e.target.value})}
                    placeholder="Any additional details..."
                    data-testid="reminder-notes-input"
                  />
                </div>

                <Button type="submit" className="w-full bg-forest hover:bg-forest-dark" data-testid="submit-reminder-btn">
                  Add Reminder
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Overdue Section */}
        {overdueReminders.length > 0 && (
          <Card className="border-0 shadow-card bg-white border-l-4 border-l-destructive" data-testid="overdue-section">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2 text-destructive">
                <AlertCircle className="w-5 h-5" />
                Overdue ({overdueReminders.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {overdueReminders.map((reminder, i) => (
                  <div 
                    key={reminder.record_id || i}
                    className="flex items-center gap-4 p-3 rounded-lg bg-destructive/5 animate-slideUp cursor-pointer hover:bg-destructive/10 transition-colors"
                    style={{ animationDelay: `${i * 0.05}s` }}
                    onClick={() => { setSelectedReminder(reminder); setEditDialogOpen(true); }}
                  >
                    <div className={`p-2 rounded-lg ${getRecordTypeClass(reminder.record_type)}`}>
                      {getRecordTypeIcon(reminder.record_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">
                        {formatRecordType(reminder.record_type)}
                        {reminder.record_type === 'appointment' && reminder.provider && (
                          <span className="text-muted-foreground font-normal"> - {reminder.provider}</span>
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">{reminder.horse_name || 'General'}</p>
                      {reminder.record_type !== 'appointment' && reminder.provider && (
                        <p className="text-xs text-muted-foreground">{reminder.provider}</p>
                      )}
                      {reminder.notes && <p className="text-xs text-muted-foreground mt-1 truncate">{reminder.notes}</p>}
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-destructive">
                        {reminder.next_due_date && format(parseISO(reminder.next_due_date), 'MMM d')}
                      </p>
                      <Badge variant="destructive" className="text-xs">Overdue</Badge>
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-muted-foreground hover:text-green-600 hover:bg-green-50"
                        onClick={(e) => handleCompleteReminder(reminder, e)}
                        title="Mark Complete"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        onClick={(e) => handleDeleteReminder(reminder.record_id, e)}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upcoming Section */}
        {upcomingReminders.length > 0 ? (
          <Card className="border-0 shadow-card bg-white" data-testid="upcoming-section">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-saddle" />
                Upcoming ({upcomingReminders.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {upcomingReminders.map((reminder, i) => (
                  <div 
                    key={reminder.record_id || i}
                    className={`flex items-center gap-4 p-3 rounded-lg ${getRecordTypeClass(reminder.record_type)} animate-slideUp cursor-pointer hover:opacity-80 transition-opacity`}
                    style={{ animationDelay: `${i * 0.05}s` }}
                    onClick={() => { setSelectedReminder(reminder); setEditDialogOpen(true); }}
                  >
                    <div className="p-2 rounded-lg bg-white/50">
                      {getRecordTypeIcon(reminder.record_type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">
                        {formatRecordType(reminder.record_type)}
                        {reminder.record_type === 'appointment' && reminder.provider && (
                          <span className="opacity-80 font-normal"> - {reminder.provider}</span>
                        )}
                      </p>
                      <p className="text-sm opacity-80">{reminder.horse_name || 'General'}</p>
                      {reminder.record_type !== 'appointment' && reminder.provider && (
                        <p className="text-xs opacity-70">{reminder.provider}</p>
                      )}
                      {reminder.notes && <p className="text-xs opacity-70 mt-1 truncate">{reminder.notes}</p>}
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium">
                        {reminder.next_due_date && format(parseISO(reminder.next_due_date), 'MMM d, yyyy')}
                      </p>
                      {isUrgent(reminder.next_due_date) && (
                        <Badge className="text-xs bg-saddle">Due Soon</Badge>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-muted-foreground hover:text-green-600 hover:bg-green-50"
                        onClick={(e) => handleCompleteReminder(reminder, e)}
                        title="Mark Complete"
                      >
                        <CheckCircle2 className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        onClick={(e) => handleDeleteReminder(reminder.record_id, e)}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ) : (
          !overdueReminders.length && (
            <Card className="border-0 shadow-card bg-white">
              <CardContent className="py-16 text-center">
                <Clock className="w-16 h-16 mx-auto mb-4 text-muted-foreground/20" />
                <h3 className="text-lg font-semibold mb-2">No Upcoming Reminders</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Click "Add Reminder" to create reminders for vaccinations, farrier visits, and more. You can assign them to a horse or keep them general.
                </p>
              </CardContent>
            </Card>
          )
        )}

        {/* By Type Summary */}
        {Object.keys(groupedReminders).length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(groupedReminders).map(([type, items]) => (
              <Card key={type} className="border-0 shadow-card bg-white">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${getRecordTypeClass(type)}`}>
                      {getRecordTypeIcon(type)}
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{items.length}</p>
                      <p className="text-sm text-muted-foreground capitalize">{formatRecordType(type)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) setSelectedReminder(null); }}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit Reminder</DialogTitle>
            </DialogHeader>
            {selectedReminder && (
              <form onSubmit={handleEditReminder} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Horse</Label>
                  <p className="text-sm text-muted-foreground">{selectedReminder.horse_name || 'General'}</p>
                </div>
                <div className="space-y-2">
                  <Label>Reminder Type</Label>
                  <Select value={selectedReminder.record_type} onValueChange={(v) => setSelectedReminder({ ...selectedReminder, record_type: v })}>
                    <SelectTrigger data-testid="edit-reminder-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="appointment">Appointment</SelectItem>
                      <SelectItem value="vaccination">Vaccination</SelectItem>
                      <SelectItem value="worming">Worming</SelectItem>
                      <SelectItem value="farrier">Farrier</SelectItem>
                      <SelectItem value="dentist">Dentist</SelectItem>
                      <SelectItem value="bodywork">Body Work</SelectItem>
                      <SelectItem value="veterinary">Veterinary</SelectItem>
                      <SelectItem value="medication">Medication</SelectItem>
                      <SelectItem value="foaling">Foaling</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Provider/Details</Label>
                  <Input 
                    value={selectedReminder.provider || ''} 
                    onChange={(e) => setSelectedReminder({ ...selectedReminder, provider: e.target.value })}
                    placeholder="Vet, Farrier name, etc."
                    data-testid="edit-reminder-provider-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Due Date</Label>
                  <Input 
                    type="date" 
                    value={selectedReminder.next_due_date || ''} 
                    onChange={(e) => setSelectedReminder({ ...selectedReminder, next_due_date: e.target.value })}
                    data-testid="edit-reminder-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea 
                    value={selectedReminder.notes || ''} 
                    onChange={(e) => setSelectedReminder({ ...selectedReminder, notes: e.target.value })}
                    placeholder="Additional notes..."
                    data-testid="edit-reminder-notes-input"
                  />
                </div>
                <Button type="submit" className="w-full bg-forest hover:bg-forest-dark" data-testid="update-reminder-btn">
                  Update Reminder
                </Button>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
